import client from "../client";
import type {
  CustomerListResponse,
  Customer360Snapshot,
  ProductCapsule,
  TimelinePage,
  UpdateContractDto,
  UpdateEmailMarketingDto,
} from "../../types/crm";

export interface SendCustomerEmailDto {
  mode: "new" | "reply";
  threadId?: string;
  subject: string;
  bodyHtml: string;
  cc?: string[];
}

export interface SentEmailResult {
  messageId: string;
  threadId: string;
  eventId: string;
}

export function fetchCustomerList(): Promise<CustomerListResponse> {
  return client.get("/admin/customers").then((res) => res.data);
}

export function fetchCustomer360(id: string): Promise<Customer360Snapshot> {
  return client.get(`/admin/customers/${id}`).then((res) => res.data);
}

export interface UpdateEmailMarketingResult {
  ok: true;
  emailMarketingEnabled: boolean;
  hotelId: string;
  created: boolean;
}

/**
 * Enable/disable email marketing. On enable for a marketing-only customer the
 * backend CREATES the hotels row from the supplied fields; a plain disable can
 * pass just `{ emailMarketingEnabled: false }`.
 */
export function updateEmailMarketing(
  id: string,
  dto: UpdateEmailMarketingDto,
): Promise<UpdateEmailMarketingResult> {
  return client
    .patch(`/admin/customers/${id}/email-marketing`, dto)
    .then((res) => res.data);
}

/** Convenience for a bare enable/disable toggle (no hotel-row edits). */
export function updateEmailMarketingEnabled(
  id: string,
  emailMarketingEnabled: boolean,
): Promise<UpdateEmailMarketingResult> {
  return updateEmailMarketing(id, { emailMarketingEnabled });
}

export interface UpdateWebsiteBuilderResult {
  ok: true;
  websiteBuilderEnabled: boolean;
  hotelId: string | null;
}

/**
 * Flag-only enable/disable of the Website Builder app. Enabling does NOT call
 * the builder or provision anything; the builder account is created lazily at
 * first SSO. Both enable and disable are one-click (no URL, no modal).
 */
export function updateWebsiteBuilder(
  id: string,
  dto: { websiteBuilderEnabled: boolean },
): Promise<UpdateWebsiteBuilderResult> {
  return client
    .patch(`/admin/customers/${id}/website-builder`, dto)
    .then((r) => r.data);
}

export function fetchCustomerTimeline(
  id: string,
  params: { limit: number; offset: number; source?: string },
): Promise<TimelinePage> {
  return client
    .get(`/admin/customers/${id}/timeline`, { params })
    .then((res) => res.data);
}

export type EmailTimelineStatus =
  | "sent"
  | "delivered"
  | "opened"
  | "clicked"
  | "bounced"
  | "complaint"
  | "rejected";

export interface EmailTimelineEntry {
  id: string;
  emailType: string;
  messageId: string | null;
  status: EmailTimelineStatus;
  sentAt: string;
  deliveredAt: string | null;
  firstOpenedAt: string | null;
  lastOpenedAt: string | null;
  openCount: number;
  firstClickedAt: string | null;
  lastClickedAt: string | null;
  clickCount: number;
  bouncedAt: string | null;
  bounceType: string | null;
  bounceSubType: string | null;
  complainedAt: string | null;
  rejectedAt: string | null;
  rejectReason: string | null;
}

export function fetchCustomerEmailTimeline(
  id: string,
): Promise<EmailTimelineEntry[]> {
  return client
    .get(`/admin/customers/${id}/email-timeline`)
    .then((res) => res.data);
}

export function updateContract(
  id: string,
  patch: UpdateContractDto,
): Promise<unknown> {
  return client
    .patch(`/admin/customers/${id}/contract`, patch)
    .then((res) => res.data);
}

export function updateTestFlag(
  id: string,
  isTestAccount: boolean,
): Promise<{ ok: true }> {
  return client
    .patch(`/admin/customers/${id}/test-flag`, { isTestAccount })
    .then((res) => res.data);
}

export function bulkTestFlag(
  userIds: string[],
  isTestAccount: boolean,
): Promise<{ ok: boolean; updated: number; failed: string[] }> {
  return client
    .patch(`/admin/customers/bulk/test-flag`, { userIds, isTestAccount })
    .then((res) => res.data);
}

export function updateCarr(
  id: string,
  carrCents: number | null,
): Promise<{ ok: true }> {
  return client
    .patch(`/admin/customers/${id}/carr`, { carrCents })
    .then((res) => res.data);
}

export function updateProductCapsules(
  id: string,
  capsules: ProductCapsule[],
): Promise<{ ok: true }> {
  return client
    .patch(`/admin/customers/${id}/product-capsules`, { capsules })
    .then((res) => res.data);
}

export function sendCustomerEmail(
  id: string,
  dto: SendCustomerEmailDto,
): Promise<SentEmailResult> {
  return client
    .post(`/admin/customers/${id}/emails`, dto)
    .then((res) => res.data);
}

export interface AskAutumnQueryResult {
  tool: string;
  ok: boolean;
  data?: unknown;
  error?: string;
}

export interface AskAutumnResponse {
  /** Server-assigned thread id. Same as the request's `threadId` if one
   *  was passed; a fresh UUID when starting a new conversation. */
  threadId: string;
  text: string;
  latency: number;
  queryResults: AskAutumnQueryResult[];
}

export function askAutumn(
  messages: Array<{ role: "user" | "assistant"; content: string }>,
  opts: { targetCustomerId?: string; threadId?: string } = {},
): Promise<AskAutumnResponse> {
  return client
    .post(`/admin/ai/ask/batch`, {
      threadId: opts.threadId,
      messages,
      targetCustomerId: opts.targetCustomerId,
    })
    .then((res) => res.data);
}

// ─── Phase 8 chat history ─────────────────────────────────────────────

export interface AdminChatThreadSummary {
  id: string;
  title: string;
  targetCustomerId: string | null;
  lastMessageAt: string | null;
  messageCount: number;
  createdAt: string;
}

export interface AdminChatStoredMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  queryResults: AskAutumnQueryResult[];
  createdAt: string;
}

export function listAdminChatThreads(): Promise<{
  threads: AdminChatThreadSummary[];
}> {
  return client.get(`/admin/ai/threads`).then((res) => res.data);
}

export function fetchAdminChatThread(threadId: string): Promise<{
  thread: AdminChatThreadSummary;
  messages: AdminChatStoredMessage[];
}> {
  return client
    .get(`/admin/ai/threads/${threadId}/messages`)
    .then((res) => res.data);
}

export function renameAdminChatThread(
  threadId: string,
  title: string,
): Promise<{ id: string; title: string }> {
  return client
    .patch(`/admin/ai/threads/${threadId}`, { title })
    .then((res) => res.data);
}

export function deleteAdminChatThread(
  threadId: string,
): Promise<{ ok: true }> {
  return client
    .delete(`/admin/ai/threads/${threadId}`)
    .then((res) => res.data);
}

// ─── Impersonation (View as Customer) ────────────────────────────────

export interface ImpersonationToken {
  accessToken: string;
  /** Unix epoch seconds at which the token expires. */
  expiresAt: number;
}

/** Mints a short-lived JWT scoped to `customerId`, audit-logged on the
 *  backend. Pair with `buildAutoLoginUrl` to send the admin to the
 *  customer-facing app. */
export function requestImpersonationToken(
  customerId: string,
): Promise<ImpersonationToken> {
  return client
    .post(`/admin/customers/${customerId}/impersonation-token`)
    .then((res) => res.data);
}

/** Base URL of the customer-facing app. Set via `REACT_APP_FRONTEND_URL`
 *  in `.env` per environment (mirrors how `REACT_APP_API_URL` is wired).
 *  Falls back to the prod URL if the env var is unset, so a misconfigured
 *  build still points somewhere sensible.
 *
 *  The token is appended as a URL *fragment* (`#token=...`), not a query,
 *  so it never enters Referer headers or server access logs of the
 *  customer-app domain. */
export function buildAutoLoginUrl(accessToken: string): string {
  const base =
    process.env.REACT_APP_FRONTEND_URL?.replace(/\/+$/, "") ||
    "https://app.autumnplatform.com";
  return `${base}/auto-login#token=${encodeURIComponent(accessToken)}`;
}

// ─── Column-spec-revised endpoints ─────────────────────────────────────

export interface PortfolioStats {
  snapshotDate: string;
  computedAt: string;
  totalCustomers: number;
  totalArr: number | null;
  totalMrr: number | null;
  /** Sum of resolved CARR (override > $6k placeholder for new customers > acvArrDefault). Excludes test accounts. */
  totalCarr: number | null;
  totalMargin30d: number | null;
  atRiskCount: number;
  /** Retired 2026-04-26 — kept on the type for compatibility but not surfaced in the cards. */
  renewalsDue30dCount: number;
  healthBandDistribution: Record<string, number>;
  productTypeDistribution: Record<string, number>;
  churnRiskDistribution: Record<string, number>;
}

export function fetchPortfolioOverview(): Promise<PortfolioStats> {
  return client.get("/admin/portfolio/overview").then((r) => r.data);
}

export interface Admin {
  id: string;
  email: string;
  firstName: string | null;
}

export function listAdmins(): Promise<{ admins: Admin[] }> {
  return client.get("/admin/customers/admins").then((r) => r.data);
}

export interface OwnerAdminRecord {
  ownershipId: string;
  adminId: string;
  adminEmail: string;
  adminFirstName: string | null;
  assignedByAdminId: string | null;
  createdAt: string;
}

export function listOwners(
  customerId: string,
): Promise<{ owners: OwnerAdminRecord[] }> {
  return client
    .get(`/admin/customers/${customerId}/owners`)
    .then((r) => r.data);
}

export function addOwner(
  customerId: string,
  adminId: string,
): Promise<OwnerAdminRecord> {
  return client
    .post(`/admin/customers/${customerId}/owners`, { adminId })
    .then((r) => r.data);
}

export function removeOwner(
  customerId: string,
  ownershipId: string,
): Promise<{ ok: true }> {
  return client
    .delete(`/admin/customers/${customerId}/owners/${ownershipId}`)
    .then((r) => r.data);
}

export interface NoteRecord {
  id: string;
  userId: string;
  authorId: string;
  body: string;
  createdAt: string;
  updatedAt?: string;
}

export function listNotes(
  customerId: string,
  opts: { limit?: number; offset?: number } = {},
): Promise<{ notes: NoteRecord[]; total: number }> {
  return client
    .get(`/admin/customers/${customerId}/notes`, { params: opts })
    .then((r) => r.data);
}

export function createNote(
  customerId: string,
  body: string,
): Promise<NoteRecord> {
  return client
    .post(`/admin/customers/${customerId}/notes`, { body })
    .then((r) => r.data);
}

export function updateNote(
  customerId: string,
  noteId: string,
  body: string,
): Promise<NoteRecord> {
  return client
    .patch(`/admin/customers/${customerId}/notes/${noteId}`, { body })
    .then((r) => r.data);
}

export function deleteNote(
  customerId: string,
  noteId: string,
): Promise<{ ok: true }> {
  return client
    .delete(`/admin/customers/${customerId}/notes/${noteId}`)
    .then((r) => r.data);
}

export function updateNextAction(
  customerId: string,
  patch: { body: string | null; dueDate: string | null },
): Promise<unknown> {
  return client
    .patch(`/admin/customers/${customerId}/next-action`, patch)
    .then((r) => r.data);
}

export function updateChurnRiskFlag(
  customerId: string,
  patch: { level: "yellow" | "red" | null; reason: string | null },
): Promise<unknown> {
  return client
    .patch(`/admin/customers/${customerId}/churn-risk-flag`, patch)
    .then((r) => r.data);
}

export interface CustomerCrmState {
  nextAction: { body: string | null; dueDate: string | null };
  churnRiskFlag: {
    level: "yellow" | "red" | null;
    reason: string | null;
  };
  monthlyAdBudget: number | null;
}

export function fetchCustomerCrmState(
  customerId: string,
): Promise<CustomerCrmState> {
  return client
    .get(`/admin/customers/${customerId}/crm-state`)
    .then((r) => r.data);
}

export interface DraftReplyResult {
  draftHtml: string;
  subject: string;
  sourceTicketId: string;
  latencyMs: number;
}

export interface DraftReplyPayload {
  threadId?: string;
  additionalInstructions?: string;
}

export function draftReply(
  customerId: string,
  payload: DraftReplyPayload,
): Promise<DraftReplyResult> {
  return client
    .post(`/admin/customers/${customerId}/emails/draft-reply`, payload)
    .then((r) => r.data);
}

export function closeThread(
  customerId: string,
  threadId: string,
): Promise<{ closed: number }> {
  return client
    .post(
      `/admin/customers/${customerId}/threads/${encodeURIComponent(threadId)}/close`,
    )
    .then((r) => r.data);
}
